/**
 * FSA Reconciliation Types — Conciliacion Fiado SA (cobranza FSA -> UNE-Credito).
 *
 * El matcher cross-account (backend credit-worker-business) responde con 5
 * buckets. Ver SPEC_MATCHER_CROSS_ACCOUNT_UNE.md.
 */

import type { SummaryColumn, ReconciliationConfig, ColumnTooltip } from './collector-reconciliation.types';

// Re-export shared types
export type { SummaryColumn, ColumnTooltip };
export { MONTH_NAMES } from './collector-reconciliation.types';

// ─── Conteos por status ─────────────────────────────────────────────────────

export interface FSAReconciliationCounts {
  ok: number;
  salioSinEntrar: number;
  entroSinSalir: number;
  sinBanco: number;
  sinCuadrar: number;
}

/** Generic detail row */
export type FSADetailRow = Record<string, unknown>;

export interface FSAReconciliationMonth {
  ok: number;
  salioSinEntrar: number;
  entroSinSalir: number;
  sinBanco: number;
  sinCuadrar: number;
  detalles: {
    ok: FSADetailRow[];
    salioSinEntrar: FSADetailRow[];
    entroSinSalir: FSADetailRow[];
    sinBanco: FSADetailRow[];
    sinCuadrar: FSADetailRow[];
  };
}

// ─── Response completa ──────────────────────────────────────────────────────

export interface FSAReconciliationResumen {
  year: number;
  direction: 'FSA_A_FMX' | 'FMX_A_FSA';
  totalTransferencias: number;
  totalMovimientosBanco: number;
  timings?: Record<string, number>;
  totals: FSAReconciliationCounts;
}

export interface FSAReconciliationData {
  resumen: FSAReconciliationResumen;
  meses: Record<string, FSAReconciliationMonth>;
}

// ─── Registro de conciliacion ───────────────────────────────────────────────

export interface FSARegisterRequest {
  year: number;
  month: number | null;
  totals: FSAReconciliationCounts;
  status: 'LIMPIO' | 'CON_DISCREPANCIAS';
  notes?: string;
}

// ─── UI state ───────────────────────────────────────────────────────────────

export interface FSADrillDownSelection {
  month: number;
  statusKey: string;
}

// ─── Column config ──────────────────────────────────────────────────────────

export const FSA_SUMMARY_COLUMNS: SummaryColumn[] = [
  {
    key: 'ok', label: 'OK', color: 'green',
    tooltip: {
      title: 'Cobranza verificada en ambas cuentas',
      description: 'La transferencia declarada tiene par completo en el banco: cargo en Fiado SA y abono en Fiado MX, misma fecha, mismo monto, misma referencia bancaria.',
    },
  },
  {
    key: 'salioSinEntrar', label: 'Solo FSA', color: 'red',
    tooltip: {
      title: 'Dinero salio de FSA pero no llego a UNE (FMX)',
      description: 'Existe cargo en el estado de cuenta de Fiado SA con el monto y fecha de la transferencia declarada, pero no aparece el abono correspondiente en Fiado MX (UNE).',
      conditions: [
        { label: 'Posibles causas', items: ['La transferencia salio a un destinatario distinto de FMX', 'Retraso en la contrapartida (raro en traspasos internos BBVA)', 'Error operativo'], severity: 'error' },
      ],
    },
  },
  {
    key: 'entroSinSalir', label: 'Solo UNE', color: 'rose',
    tooltip: {
      title: 'Dinero aparecio en UNE (FMX) sin origen conocido',
      description: 'Existe abono en Fiado MX (UNE) con el monto y fecha de la transferencia declarada, pero no aparece el cargo correspondiente en Fiado SA.',
      conditions: [
        { label: 'Posibles causas', items: ['El fondeo vino de una cuenta distinta a FSA', 'Imputacion bancaria erronea', 'Error operativo'], severity: 'error' },
      ],
    },
  },
  {
    key: 'sinBanco', label: 'Sin banco', color: 'amber',
    tooltip: {
      title: 'La transferencia declarada no aparece en el banco',
      description: 'No existe ni cargo en Fiado SA ni abono en Fiado MX con el monto y fecha declarados.',
      conditions: [
        { label: 'Posibles causas', items: ['La transferencia no se ejecuto', 'Fecha o monto capturados con error en la declaracion', 'Traspaso interno pendiente de reflejar en el edo cta'], severity: 'error' },
      ],
    },
  },
  {
    key: 'sinCuadrar', label: 'Sin cuadrar', color: 'slate',
    tooltip: {
      title: 'Falta informacion bancaria del mes',
      description: 'El estado de cuenta de Fiado SA o Fiado MX del mes no esta cargado o clasificado todavia. No se puede afirmar presencia ni ausencia de la transferencia hasta que el cuadre general procese el mes.',
    },
  },
];

// ─── Entity config (for comparison modal) ───────────────────────────────────

export const FSA_RECONCILIATION_CONFIG: ReconciliationConfig = {
  label: 'Cobranza FSA -> FMX',
  description: 'Cruza cobranzas declaradas a UNE-Credito con el par bancario cargo FSA + abono FMX',
  color: 'emerald',
  entityA: {
    name: 'Transferencia declarada',
    color: 'emerald',
    fields: [
      { key: 'transferId', label: 'Transfer ID', format: 'mono' },
      { key: 'transferDate', label: 'Fecha', format: 'date' },
      { key: 'totalAmountMxn', label: 'Monto MXN', format: 'currency' },
      { key: 'bankReference', label: 'Clave Rastreo', format: 'mono' },
      { key: 'transferStatus', label: 'Status' },
      { key: 'periodStart', label: 'Periodo Inicio', format: 'date' },
      { key: 'periodEnd', label: 'Periodo Fin', format: 'date' },
      { key: 'recordsCount', label: '# Cobros' },
      { key: 'creditsCount', label: '# Creditos' },
      { key: 'totalPrincipalMxn', label: 'Capital', format: 'currency' },
      { key: 'totalPartnerInterestMxn', label: 'Interes UNE-Credito', format: 'currency' },
      { key: 'totalFiadoInterestMxn', label: 'Interes Fiado', format: 'currency' },
    ],
  },
  entityB: {
    name: 'Par bancario FSA -> FMX',
    color: 'cyan',
    fields: [
      { key: 'salidaCuenta', label: 'Cuenta salida' },
      { key: 'salidaFecha', label: 'Fecha salida', format: 'date' },
      { key: 'salidaMonto', label: 'Monto salida', format: 'currency' },
      { key: 'salidaReferencia', label: 'Ref. salida' },
      { key: 'salidaCodigo', label: 'Codigo salida' },
      { key: 'entradaCuenta', label: 'Cuenta entrada' },
      { key: 'entradaFecha', label: 'Fecha entrada', format: 'date' },
      { key: 'entradaMonto', label: 'Monto entrada', format: 'currency' },
      { key: 'entradaReferencia', label: 'Ref. entrada' },
    ],
  },
  matchStatuses: ['ok'],
};
